import * as THREE from 'three';
import { createWheelMaterial } from './carShader';
import { wheelGeometry, type WheelStyle } from './meshes';
import { isBlenderWheel, onRimModels, requestWheelStyle, rimGeometry, rimLodFor } from './rimModels';

/**
 * Every car's wheels drawn as a handful of InstancedMeshes (one per wheel style) instead of three
 * meshes per car. Rigs allocate slots at spawn and write their wheel matrices each frame (or hide them:
 * a far impostor). The batch keeps the slots on the CPU and, once per frame (`flushWheelBatches`, called
 * by the render system right before it draws, with that frame's view frustum), packs only the SHOWN
 * wheels that are in view to the front of the instance buffer and draws that many: a hidden or
 * off-screen slot costs nothing on the GPU. (Before, every slot up to the high-water mark was drawn,
 * hidden ones as zero-scale instances: ~145 k triangles of parked and off-screen wheels.) The cull uses
 * the frustum of the frame being drawn, so a camera cut never shows a frame of wheel-less cars.
 */
const CAPACITY = 640;
const BURNT = new THREE.Color(0.12, 0.11, 0.1);
const CLEAN = new THREE.Color(1, 1, 1);
/** A wheel is drawn while a sphere this big (m) around its hub touches the view: the largest wheel's
 * radius (~0.6 m) with a wide margin, so nothing is ever cut at the screen edge. */
const WHEEL_CULL_RADIUS = 1.5;
const _sphere = new THREE.Sphere(new THREE.Vector3(), WHEEL_CULL_RADIUS);

interface Batch {
  mesh: THREE.InstancedMesh;
  free: number[];
  high: number;
  /** Per slot: the last matrix written, whether it is shown, whether it is burnt, its rim colour. */
  mats: Float32Array;
  shown: Uint8Array;
  burnt: Uint8Array;
  /** rgb (linear) + w = 1 when the rim is painted (Pike's spray bay; w = 0 keeps the wheel's own finish). */
  rims: Float32Array;
  /** Packed per drawn instance, like the matrices: the wheel shader's `rimTint`. */
  rimAttr: THREE.InstancedBufferAttribute;
  dirty: boolean;
}

let scene: THREE.Object3D | null = null;
const batches = new Map<WheelStyle, Batch>();
let material: THREE.MeshStandardMaterial | null = null;
/** The detail tier every wheel draws at (vehicleAmbient.tier: 0 LOW, 1 MEDIUM, 2 HIGH). */
let tier = 2;

/**
 * The geometry a style draws at the current tier: a Blender wheel's high / mid / low LOD once its pack is in
 * (rimModels.ts: the premium rims and the period wheels alike), else the procedural wheel (full on HIGH, lite below).
 */
function geometryFor(style: WheelStyle): THREE.BufferGeometry {
  return (isBlenderWheel(style) ? rimGeometry(style, rimLodFor(tier)) : null) ?? wheelGeometry(style, tier < 2);
}

function swapGeometry(style: WheelStyle, b: Batch): void {
  const g = geometryFor(style);
  if (b.mesh.geometry === g) return;
  b.mesh.geometry = g;
  g.setAttribute('rimTint', b.rimAttr);
}

/** Detail tier for every wheel: each batch swaps to the geometry for the tier (lite on MEDIUM/LOW; a rim's LOD). */
export function setWheelDetail(detailTier: number): void {
  if (detailTier === tier) return;
  tier = detailTier;
  for (const [style, b] of batches) swapGeometry(style, b);
}

// A rim's real geometry arrives after its batch was made on the stand-in: swap the batch over.
onRimModels((style) => {
  const b = batches.get(style as WheelStyle);
  if (b) swapGeometry(style as WheelStyle, b);
});

function batch(style: WheelStyle): Batch {
  let b = batches.get(style);
  if (!b) {
    material ??= createWheelMaterial();
    // The first car on a style fetches its pack: the period wheels' (every scene with a car), or the premium
    // rims' (never in a scene where nobody drives one).
    requestWheelStyle(style);
    const mesh = new THREE.InstancedMesh(geometryFor(style), material, CAPACITY);
    mesh.name = `wheels-${style}`;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.count = 0;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.setColorAt(0, CLEAN); // creates instanceColor
    mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    // The cached geometry belongs to this batch alone (one batch per style), so the per-instance rim tint can
    // live on it; a detail swap moves it to the other geometry.
    const rimAttr = new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 4), 4);
    rimAttr.setUsage(THREE.DynamicDrawUsage);
    mesh.geometry.setAttribute('rimTint', rimAttr);
    b = { mesh, free: [], high: 0, mats: new Float32Array(CAPACITY * 16), shown: new Uint8Array(CAPACITY), burnt: new Uint8Array(CAPACITY), rims: new Float32Array(CAPACITY * 4), rimAttr, dirty: false };
    batches.set(style, b);
    scene?.add(mesh);
  }
  return b;
}

/** Called by the vehicle view system with its scene. */
export function attachWheelBatches(s: THREE.Object3D): void {
  scene = s;
  for (const b of batches.values()) s.add(b.mesh);
}

export function detachWheelBatches(): void {
  for (const b of batches.values()) b.mesh.removeFromParent();
  scene = null;
}

export interface WheelSlot {
  style: WheelStyle;
  index: number;
}

/** A slot for one wheel (hidden until a matrix is written). */
export function allocWheel(style: WheelStyle): WheelSlot | null {
  const b = batch(style);
  let index = b.free.pop();
  if (index === undefined) {
    if (b.high >= CAPACITY) return null;
    index = b.high++;
  }
  b.shown[index] = 0;
  b.burnt[index] = 0;
  b.rims[index * 4 + 3] = 0;
  return { style, index };
}

export function freeWheel(slot: WheelSlot): void {
  const b = batches.get(slot.style);
  if (!b) return;
  if (b.shown[slot.index]) b.dirty = true;
  b.shown[slot.index] = 0;
  b.burnt[slot.index] = 0;
  b.free.push(slot.index);
}

/** Show the wheel with this world matrix, or hide it (`null`). Takes effect at the next flush. */
export function setWheelMatrix(slot: WheelSlot, m: THREE.Matrix4 | null): void {
  const b = batches.get(slot.style)!;
  if (m) {
    b.mats.set(m.elements, slot.index * 16);
    b.shown[slot.index] = 1;
    b.dirty = true;
  } else if (b.shown[slot.index]) {
    b.shown[slot.index] = 0;
    b.dirty = true;
  }
}

const _rim = new THREE.Color();

/** Paint the wheel's rim (hex, sRGB) or give it back its own finish (`null`). Takes effect at the next flush. */
export function setWheelRim(slot: WheelSlot, hex: number | null): void {
  const b = batches.get(slot.style)!;
  const o = slot.index * 4;
  if (hex === null) b.rims[o + 3] = 0;
  else {
    _rim.setHex(hex);
    b.rims[o] = _rim.r;
    b.rims[o + 1] = _rim.g;
    b.rims[o + 2] = _rim.b;
    b.rims[o + 3] = 1;
  }
  if (b.shown[slot.index]) b.dirty = true;
}

export function setWheelBurnt(slot: WheelSlot, burnt: boolean): void {
  const b = batches.get(slot.style)!;
  const v = burnt ? 1 : 0;
  if (b.burnt[slot.index] === v) return;
  b.burnt[slot.index] = v;
  if (b.shown[slot.index]) b.dirty = true;
}

/**
 * Pack the shown wheels of each batch to the front of its instance buffers and draw only those. With
 * a `view` frustum (the render system, every frame, right before drawing) wheels outside it are left
 * out as well; without one (no renderer: tests, headless) only changed batches are repacked. Uploads
 * only the packed range.
 */
export function flushWheelBatches(view: THREE.Frustum | null = null): void {
  for (const b of batches.values()) {
    if (!b.dirty && !view) continue;
    b.dirty = false;
    const mats = b.mesh.instanceMatrix.array as Float32Array;
    const cols = b.mesh.instanceColor!.array as Float32Array;
    const tints = b.rimAttr.array as Float32Array;
    const src = b.mats;
    let n = 0;
    for (let i = 0; i < b.high; i++) {
      if (!b.shown[i]) continue;
      const o = i * 16;
      if (view) {
        _sphere.center.set(src[o + 12]!, src[o + 13]!, src[o + 14]!);
        if (!view.intersectsSphere(_sphere)) continue;
      }
      mats.set(src.subarray(o, o + 16), n * 16);
      const c = b.burnt[i] ? BURNT : CLEAN;
      cols[n * 3] = c.r;
      cols[n * 3 + 1] = c.g;
      cols[n * 3 + 2] = c.b;
      // A painted rim burns with the rest of the wheel.
      const r = i * 4;
      const k = b.burnt[i] ? BURNT.r : 1;
      tints[n * 4] = b.rims[r]! * k;
      tints[n * 4 + 1] = b.rims[r + 1]! * k;
      tints[n * 4 + 2] = b.rims[r + 2]! * k;
      tints[n * 4 + 3] = b.rims[r + 3]!;
      n++;
    }
    b.mesh.count = n;
    if (n === 0) continue; // count 0 draws nothing: no upload needed
    for (const attr of [b.mesh.instanceMatrix, b.mesh.instanceColor!, b.rimAttr]) {
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, n * attr.itemSize);
      attr.needsUpdate = true;
    }
  }
}

/** Wheels drawn this frame per style (tests and perf read-outs). */
export function wheelDrawCounts(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [style, b] of batches) out[style] = b.mesh.count;
  return out;
}
